import { useMemo, useState } from 'react';
import { Search, Checkbox } from '@carbon/react';
import type { FacetBucket } from '../types';
import { toggleAllVisible } from './select-all';
import { BucketCheckboxList } from './bucket-checkbox-list';
import { PasteListControl } from 'components/paste-list';
import style from './filter-modal.module.scss';

interface Props {
  /** Field id, used to namespace input/checkbox ids. */
  id: string;
  /** Search box label + placeholder + select-all header text. */
  label: string;
  facetBuckets: FacetBucket[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** Empty-state text when no buckets are loaded. */
  emptyHint?: string;
  /** Noun appended after each option's count (e.g. "Documents"). */
  countNoun?: string;
}

// Always-visible checkbox list with a client-side search box and Select All.
// The search only narrows the visible buckets in the browser; every option is
// already loaded from the facet aggregation, so no server round-trip is needed.
export const FilterListGroup = ({
  id,
  label,
  facetBuckets,
  selectedIds,
  onChange,
  emptyHint = 'Run a search first to see available options',
  countNoun,
}: Props) => {
  const [searchText, setSearchText] = useState('');

  const visibleBuckets = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return facetBuckets;
    return facetBuckets.filter((b) => b.name.toLowerCase().includes(q));
  }, [facetBuckets, searchText]);

  const allVisibleSelected =
    visibleBuckets.length > 0 &&
    visibleBuckets.every((b) => selectedIds.includes(b.id));
  const someVisibleSelected = visibleBuckets.some((b) =>
    selectedIds.includes(b.id)
  );

  const handleToggleAll = () => {
    onChange(
      toggleAllVisible(
        selectedIds,
        visibleBuckets.map((b) => b.id),
        allVisibleSelected
      )
    );
  };

  const handleToggle = (bucketId: string, checked: boolean) => {
    const next = checked
      ? [...selectedIds, bucketId]
      : selectedIds.filter((v) => v !== bucketId);
    onChange(next);
  };

  return (
    <div className={style.comboGroup}>
      <Search
        id={`list-search-${id}`}
        labelText={`Search ${label}`}
        placeholder={`Search ${label.toLowerCase()}...`}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onClear={() => setSearchText('')}
        size="md"
      />
      <PasteListControl
        id={`list-${id}`}
        label={label}
        options={facetBuckets}
        selectedIds={selectedIds}
        onApply={onChange}
      />
      {facetBuckets.length > 0 ? (
        <div className={style.listBox}>
          {/* Gray header row = select-all + the field name (mock parity). */}
          <div className={style.listHeaderRow}>
            <Checkbox
              id={`select-all-${id}`}
              labelText={label}
              checked={allVisibleSelected}
              indeterminate={someVisibleSelected && !allVisibleSelected}
              disabled={visibleBuckets.length === 0}
              onChange={handleToggleAll}
            />
          </div>
          <BucketCheckboxList
            idPrefix={`list-${id}`}
            className={style.listBuckets}
            buckets={visibleBuckets}
            selectedIds={selectedIds}
            emptyHint="No matching values"
            countNoun={countNoun}
            onToggle={handleToggle}
          />
        </div>
      ) : (
        <p className={style.emptyHint}>{emptyHint}</p>
      )}
    </div>
  );
};
